'use client'

import {
    Building2,
    CreditCard,
    Coins,
    Gift,
    History,
    Receipt,
    Shield,
    SlidersHorizontal,
    Ticket,
    User,
    type LucideIcon,
} from 'lucide-react'
import { useWithUpperKeyboardShortcutsRestoration } from '@/features/keybindings/keybinding-module-context'
import { BillingPaymentNotice } from '@/features/subscription/billing/billing-payment-notice'
import { SETTINGS_TABS, type ISettingsTabId } from '@/setup/routing/settings-route'
import { useSettingsModal } from './settings-modal-context'
import { SettingsModalShell, type ISettingsShellTab } from './settings-modal-shell'
import { PlansTab } from './tabs/plans/plans-tab'
import { BillingInvoicesTab } from './tabs/billing-invoices/billing-invoices-tab'
import { CreditsTab } from './tabs/credits/credits-tab'
import { UserTab } from './tabs/user/user-tab'
import { OrganizationTab } from './tabs/organization/organization-tab'
import { PrivacyTab } from './tabs/privacy/privacy-tab'
import { TopupsTab } from './tabs/topups/topups-tab'
import { VouchersTab } from './tabs/vouchers/vouchers-tab'
import { ReferralTab } from './tabs/referral/referral-tab'
import { AppCustomizationTab } from './tabs/app-customization/app-customization-tab'

const TAB_ICONS: Record<ISettingsTabId, LucideIcon> = {
    plans: CreditCard,
    billing: Receipt,
    credits: History,
    topups: Coins,
    vouchers: Ticket,
    user: User,
    organization: Building2,
    referral: Gift,
    'app-customization': SlidersHorizontal,
    privacy: Shield,
}

const SHELL_TABS: ISettingsShellTab[] = SETTINGS_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: TAB_ICONS[tab.id],
}))

export const SettingsModal = () => {
    const { isOpen, closeSettingsModal, options, setOptions } = useSettingsModal()
    const { activeTab, highlightedPlan } = options

    const onEscape = useWithUpperKeyboardShortcutsRestoration((e: KeyboardEvent) => {
        e.preventDefault()
        closeSettingsModal()
    })

    const isBillingTab =
        activeTab === 'plans' ||
        activeTab === 'billing' ||
        activeTab === 'credits' ||
        activeTab === 'topups' ||
        activeTab === 'vouchers'

    return (
        <SettingsModalShell
            open={isOpen}
            onClose={closeSettingsModal}
            // `modal={false}` keeps everything portaled inside the Clerk components clickable (e.g. dropdowns).
            modal={false}
            onEscapeKeyDown={onEscape}
            tabs={SHELL_TABS}
            activeTab={activeTab}
            onTabChange={(id) => setOptions({ activeTab: id as ISettingsTabId })}
            // Payment notice for billing tabs. The wrapper spacing lives inside the notice so nothing
            // (and no empty space) renders when no payment is due.
            subHeader={isBillingTab ? <BillingPaymentNotice className="px-6 pt-4" /> : null}
        >
            {activeTab === 'plans' && <PlansTab highlightedPlan={highlightedPlan} />}
            {activeTab === 'billing' && <BillingInvoicesTab />}
            {activeTab === 'credits' && <CreditsTab />}
            {activeTab === 'topups' && <TopupsTab />}
            {activeTab === 'vouchers' && <VouchersTab />}
            {activeTab === 'user' && <UserTab />}
            {activeTab === 'organization' && <OrganizationTab />}
            {activeTab === 'referral' && <ReferralTab />}
            {activeTab === 'app-customization' && <AppCustomizationTab />}
            {activeTab === 'privacy' && <PrivacyTab />}
        </SettingsModalShell>
    )
}
